import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeLanding.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeSectionList from '../components/MicrositeSectionList/MicrositeSectionList'

const SECTION_ORDER = [
    'heroSection',
    'aboutSection',
    'categoriesSection',
    'featuresSection',
    'ourTeamSection',
    'testimonialsSection',
    'faqSection',
    'contactSection',
    'footerSection',
    'notFoundSection'
]

const MicrositeLanding = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [reordering, setReordering] = useState(false)
    const [toast, setToast] = useState(null)
    const [sections, setSections] = useState([])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadLanding = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetStoreConfig')
        if (res?.status === 'success') {
            const existing = res.response?.landing || []
            const existingMap = Object.fromEntries(existing.map((s) => [s.sectionKey, s]))

            // Ensure every known section type appears, even if not yet saved
            const merged = SECTION_ORDER.map((key, idx) => ({
                sectionKey: key,
                isVisible: existingMap[key]?.isVisible ?? true,
                sortOrder: existingMap[key]?.sortOrder ?? idx,
                updatedOn: existingMap[key]?.updatedOn ?? null
            })).sort((a, b) => a.sortOrder - b.sortOrder)

            setSections(merged)
        } else {
            showToast('Could not load landing page sections. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadLanding()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const handleReorder = async (order) => {
        // Optimistic update
        const reordered = order.map(({ sectionKey, sortOrder }) => {
            const existing = sections.find((s) => s.sectionKey === sectionKey)
            return { ...existing, sortOrder }
        })
        setSections(reordered)

        setReordering(true)
        const res = await post('micrositeReorderLandingSections', { order })
        setReordering(false)

        if (res?.status === 'success') {
            showToast('Section order saved')
        } else {
            showToast('Could not save new order. Please try again.', 'error')
            loadLanding()
        }
    }

    const handleToggleVisible = async (sectionKey, isVisible) => {
        // Optimistic update
        setSections((prev) => prev.map((s) => (s.sectionKey === sectionKey ? { ...s, isVisible } : s)))

        const target = sections.find((s) => s.sectionKey === sectionKey)
        const res = await post('micrositeUpdateLandingSection', {
            sectionKey,
            isVisible,
            sortOrder: target?.sortOrder ?? 0,
            content: {}
        })

        if (res?.status === 'success') {
            showToast(isVisible ? 'Section shown' : 'Section hidden')
        } else {
            showToast('Could not update visibility', 'error')
            setSections((prev) => prev.map((s) => (s.sectionKey === sectionKey ? { ...s, isVisible: !isVisible } : s)))
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading your landing page…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Landing page"
                subtitle="Drag to reorder sections, toggle visibility, or edit each section's content."
            />

            {reordering && <div className={styles.savingHint}>Saving order…</div>}

            <MicrositeSectionList
                sections={sections}
                onReorder={handleReorder}
                onToggleVisible={handleToggleVisible}
            />
        </div>
    )
}

export default MicrositeLanding